import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, ArchiveRestore, Camera, ImageUp, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDestructiveDialog } from '@/components/confirm-destructive-dialog';
import { LinkEditDialog } from './link-edit-dialog';
import { useToast } from '@/components/ui/toast';
import {
  useDeleteLink,
  useRegenerateScreenshot,
  useRetryLink,
  useUpdateLink,
  useUploadScreenshot,
} from '@/hooks/use-links';
import { describeError } from '@/lib/api-client';
import { truncate } from '@/lib/format';
import { prepareScreenshotUpload } from '@/lib/image';
import type { LinkProgress } from '@/hooks/use-link-progress';
import type { LinkDetail } from '@/types/api';

/**
 * Admin-only action bar. Rendering is gated on the role from GET /api/v1/me, but that is a UX
 * decision — the API authorises each of these endpoints independently.
 */
export function LinkAdminActions({ link, progress }: { link: LinkDetail; progress: LinkProgress }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const updateLink = useUpdateLink();
  const retryLink = useRetryLink();
  const regenerateScreenshot = useRegenerateScreenshot();
  const uploadScreenshot = useUploadScreenshot();
  const deleteLink = useDeleteLink();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preparingUpload, setPreparingUpload] = useState(false);

  const onError = (title: string) => (error: Error) =>
    toast({ title, description: describeError(error), variant: 'error' });

  const onScreenshotFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // Let the same file be re-selected after an error.
    if (!file) return;

    setPreparingUpload(true);
    try {
      const body = await prepareScreenshotUpload(file);
      uploadScreenshot.mutate(
        { linkId: link.linkId, body },
        {
          onSuccess: () =>
            toast({
              title: 'Screenshot updated',
              description: 'The uploaded image is now shown for this link.',
              variant: 'success',
            }),
          onError: onError('Could not upload the screenshot'),
        },
      );
    } catch (error) {
      toast({
        title: 'Could not read that image',
        description: describeError(error),
        variant: 'error',
      });
    } finally {
      setPreparingUpload(false);
    }
  };

  const toggleArchived = () => {
    const nextArchived = !link.archived;
    updateLink.mutate(
      { linkId: link.linkId, body: { archived: nextArchived } },
      {
        onSuccess: () =>
          toast({
            title: nextArchived ? 'Link archived' : 'Link restored',
            description: nextArchived
              ? 'It is hidden from the default catalogue view.'
              : 'It is visible in the catalogue again.',
            variant: 'success',
          }),
        onError: onError('Could not change the archive state'),
      },
    );
  };

  return (
    <section
      aria-label="Administrator actions"
      className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 p-3"
    >
      <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Admin
      </span>

      <Button type="button" variant="outline" size="sm" onClick={() => setEditOpen(true)}>
        <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
        Edit
      </Button>

      <Button
        type="button"
        variant="outline"
        size="sm"
        loading={updateLink.isPending}
        onClick={toggleArchived}
      >
        {link.archived ? (
          <ArchiveRestore aria-hidden="true" className="h-3.5 w-3.5" />
        ) : (
          <Archive aria-hidden="true" className="h-3.5 w-3.5" />
        )}
        {link.archived ? 'Restore' : 'Archive'}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="sm"
        // Busy until the capture it queued has finished, not just until the request returns.
        loading={regenerateScreenshot.isPending || progress.capturing}
        onClick={() =>
          regenerateScreenshot.mutate(link.linkId, {
            onSuccess: () =>
              toast({
                title: 'Screenshot regeneration queued',
                description: 'This page will update when the capture finishes.',
                variant: 'success',
              }),
            onError: onError('Could not queue the screenshot'),
          })
        }
      >
        <Camera aria-hidden="true" className="h-3.5 w-3.5" />
        Regenerate screenshot
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onScreenshotFileSelected}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        loading={preparingUpload || uploadScreenshot.isPending}
        onClick={() => fileInputRef.current?.click()}
        title="Upload an image to use when the automated capture is blocked"
      >
        <ImageUp aria-hidden="true" className="h-3.5 w-3.5" />
        Upload screenshot
      </Button>

      <Button
        type="button"
        variant="outline"
        size="sm"
        loading={retryLink.isPending || progress.enriching}
        onClick={() =>
          retryLink.mutate(link.linkId, {
            onSuccess: () =>
              toast({
                title: 'Enrichment re-queued',
                description: 'This page will update as it progresses — usually within a minute.',
                variant: 'success',
              }),
            onError: onError('Could not re-queue enrichment'),
          })
        }
      >
        <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
        Retry enrichment
      </Button>

      <Button
        type="button"
        variant="destructive"
        size="sm"
        className="ml-auto"
        onClick={() => setDeleteOpen(true)}
      >
        <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
        Delete
      </Button>

      <LinkEditDialog link={link} open={editOpen} onOpenChange={setEditOpen} />

      <ConfirmDestructiveDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this link permanently?"
        description={
          <>
            The record for{' '}
            <span className="font-medium">{truncate(link.title ?? link.originalUrl, 90)}</span> will
            be removed, including its screenshot. If the same URL is shared again later it will be
            collected as a brand-new link with a new first sharer. Consider archiving instead.
          </>
        }
        confirmPhrase="DELETE"
        pending={deleteLink.isPending}
        onConfirm={() =>
          deleteLink.mutate(link.linkId, {
            onSuccess: () => {
              toast({ title: 'Link deleted', variant: 'success' });
              setDeleteOpen(false);
              void navigate('/', { replace: true });
            },
            onError: onError('Could not delete the link'),
          })
        }
      />
    </section>
  );
}
